import React from "react";
import { IoLockClosedOutline } from "react-icons/io5";
import GmailFilterSetupCard from "./GmailFilterSetupCard";
import LookbackPeriodControl from "./LookbackPeriodControl";
import { formatSimpleLookbackRangeLabel } from "./lookbackPeriod";
import {
  canFinishGmailConnectorSetup,
  getConnectorSetupPrimaryLabel,
  getSimpleConnectCopy,
  shouldAutoFinishConnectorSetup,
} from "./businessDigestSetup";

/**
 * Unified Gmail connector setup: filters, OAuth CTA, and optional lookback options.
 *
 * @example
 * <GmailConnectorSetupPanel
 *   senderPatterns="no-reply@zoom.us"
 *   subjectKeywords="meeting"
 *   onSenderPatternsChange={setSenders}
 *   onSubjectKeywordsChange={setKeywords}
 *   sourceAuthorized={false}
 *   onConnect={() => startOAuth()}
 *   onFinish={() => submitSetup()}
 * />
 */
const GmailConnectorSetupPanel = ({
  senderPatterns,
  subjectKeywords,
  onSenderPatternsChange,
  onSubjectKeywordsChange,
  lookbackDays,
  onLookbackDaysChange,
  lookbackConfig,
  lookbackLoading = false,
  showAdvancedOptions = false,
  onShowAdvancedOptionsChange,
  sourceAuthorized = false,
  connectLoading = false,
  submitting = false,
  onConnect,
  onFinish,
  compact = false,
  setupFailed = false,
  onboardingLayout = true,
}) => {
  const copy = getSimpleConnectCopy("gmail");
  const lookbackSummary = formatSimpleLookbackRangeLabel(lookbackDays);
  const canFinish = sourceAuthorized && canFinishGmailConnectorSetup(
    senderPatterns,
    subjectKeywords
  );
  const shouldAutoFinish = shouldAutoFinishConnectorSetup({
    sourceAuthorized,
    canFinish,
    setupFailed,
  });
  const primaryDisabled =
    submitting || connectLoading || lookbackLoading || shouldAutoFinish;

  const handlePrimaryClick = () => {
    if (primaryDisabled) return;
    if (!sourceAuthorized) {
      onConnect?.();
      return;
    }
    if (canFinish) {
      onFinish?.();
    }
  };

  const primaryLabel = getConnectorSetupPrimaryLabel({
    copy,
    connectLoading,
    submitting,
    sourceAuthorized,
    canFinish,
    shouldAutoFinish,
  });

  const showLockIcon = !sourceAuthorized && !connectLoading && !submitting;

  return (
    <div
      className={`business-simple-connect business-simple-connect--gmail${
        compact ? " business-simple-connect--compact" : ""
      }${onboardingLayout ? " business-simple-connect--onboarding" : ""}`}
    >
      <GmailFilterSetupCard
        senderPatterns={senderPatterns}
        subjectKeywords={subjectKeywords}
        onSenderPatternsChange={onSenderPatternsChange}
        onSubjectKeywordsChange={onSubjectKeywordsChange}
      />

      <div className="business-simple-connect__actions">
        {!showAdvancedOptions ? (
          <p className="business-simple-connect__lookback-summary">
            Syncs the last <strong>{lookbackSummary}</strong>.{" "}
            <button
              type="button"
              className="business-simple-connect__change-link"
              onClick={() => onShowAdvancedOptionsChange?.(true)}
            >
              Change
            </button>
          </p>
        ) : (
          <>
            <LookbackPeriodControl
              value={lookbackDays}
              onChange={onLookbackDaysChange}
              quickPresets={lookbackConfig.workflowQuickPresets}
              minDays={lookbackConfig.minDays}
              disabled={lookbackLoading}
            />
            <button
              type="button"
              className="business-simple-connect__change-link business-simple-connect__change-link--collapse"
              onClick={() => onShowAdvancedOptionsChange?.(false)}
            >
              Hide options
            </button>
          </>
        )}

        {sourceAuthorized ? (
          <div className="business-simple-connect__connected">
            <div className="business-connected-pill">
              <span className="business-connected-pill__dot" aria-hidden="true" />
              Connected
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="primary-button business-simple-connect__cta"
          onClick={() => handlePrimaryClick()}
          disabled={primaryDisabled || (sourceAuthorized && !canFinish)}
          aria-busy={connectLoading || submitting}
        >
          {connectLoading || submitting ? (
            <span className="business-setup-btn__spinner" aria-hidden="true" />
          ) : showLockIcon ? (
            <IoLockClosedOutline aria-hidden="true" />
          ) : null}
          <span>{primaryLabel}</span>
        </button>
      </div>
    </div>
  );
};

export default GmailConnectorSetupPanel;
